import { FilteredGridPage, GRID_PAGES } from 'grid/FilteredGridPage'
import { serverSideConfigs } from 'localization'
import { PagesAdmin } from 'pagesAdmin'
import { ProductTemplatePage } from 'productTemplate/ProductTemplatePage'
import { GetAPServerSideProps, getAPServerSideProps } from 'shared/utils/getAPServerSideProps'
import { GetPDPServerSideProps, getPDPServerSideProps } from 'shared/utils/getPDPServerSideProps'
import { GetPLPServerSideProps, getPLPServerSideProps } from 'shared/utils/getPLPServerSideProps'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'

type SlugPageProps = GetPDPServerSideProps & GetPLPServerSideProps & GetAPServerSideProps

const SlugPage = ({
  adminPageData,
  breadcrumbData,
  consumerSearchResult,
  consumerSearchTrendingResults,
  error,
  errorStatus,
  hostname,
  baseCSSearchState,
  consumerSearchState,
  isAutomation,
  isPagesAdmin,
  isPdp,
  locale,
  pathname,
  productTemplate,
  queryParamSearchState,
  resultsState,
  slug,
  ssrDarwinGroup,
  trendingResultsState,
  urlSize,
}: SlugPageProps) => {
  if (isPagesAdmin) {
    return <PagesAdmin {...adminPageData} />
  }

  return isPdp ? (
    <ProductTemplatePage
      errorStatus={errorStatus}
      hostname={hostname}
      locale={locale}
      pathname={pathname}
      productTemplate={productTemplate}
      urlSize={urlSize}
      isAutomation={isAutomation}
    />
  ) : (
    <FilteredGridPage
      consumerSearchResult={consumerSearchResult}
      consumerSearchTrendingResults={consumerSearchTrendingResults}
      breadcrumbData={breadcrumbData}
      error={error}
      hostname={hostname}
      baseCSSearchState={baseCSSearchState}
      consumerSearchState={consumerSearchState}
      queryParamSearchState={queryParamSearchState}
      resultsState={resultsState}
      slug={slug}
      ssrDarwinGroup={ssrDarwinGroup}
      trendingResultsState={trendingResultsState}
    />
  )
}

export default SlugPage

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { locale, params, res } = context
  const { slug } = params || {}

  //   1. Check if grid page (PLP) => return props for PLP.
  if (GRID_PAGES.has(slug[0])) {
    const [pageProps, i18nProps, plpProps] = await Promise.all([
      getServerPageProps(context),
      serverSideConfigs(res, locale),
      getPLPServerSideProps({ context, slug }),
    ])

    return {
      props: { ...pageProps, ...i18nProps, ...JSON.parse(JSON.stringify(plpProps)) },
    }
  }

  //   2. If not grid page, check if admin page => return props for admin page.
  const adminPageProps = await getAPServerSideProps({ context, slug })
  if (adminPageProps) {
    const [pageProps, i18nProps] = await Promise.all([
      getServerPageProps(context),
      serverSideConfigs(res, locale),
    ])
    return {
      props: { ...pageProps, ...i18nProps, ...adminPageProps },
    }
  }

  //   3. Last, it could be a product page (PDP) => return PDP props.
  if (slug && slug.length > 0) {
    const [pageProps, i18nProps, pdpProps] = await Promise.all([
      getServerPageProps(context),
      serverSideConfigs(res, locale),
      getPDPServerSideProps({ slug, context }),
    ])

    return {
      props: { ...pageProps, ...i18nProps, ...pdpProps },
    }
  }

  //   Catch all default props.
  return {
    props: {
      ...(await getServerPageProps(context)),
      ...(await serverSideConfigs(res, locale)),
    },
  }
}
